import { format, parse } from 'date-fns';
import { convertTokens } from '@date-fns/upgrade/v2';
import { forOwn } from 'lodash';
import getLocalDate from '../getLocalDate';
import config from '../../config';

const RUNTIME_FORMAT = 'do LLL, KK:mm:a';

const convertModelTimingsToForecastTimes = ({ run, onlineAt }, { offset, abbr }) => {
  if (!run || !onlineAt) return;

  const offsetMilliseconds = offset * 3600000;
  const localRunTimeTimestamp = parse(run, 'yyyyMMddHH', new Date().setMinutes(0)).getTime() + offsetMilliseconds;
  const localOnlineTimeTimestamp = parse(onlineAt, 'tt', new Date()).getTime() + offsetMilliseconds;

  return {
    run: `${format(new Date(localRunTimeTimestamp), RUNTIME_FORMAT)} ${abbr}`,
    online: `${format(new Date(localOnlineTimeTimestamp), RUNTIME_FORMAT)} ${abbr}`,
  };
};

const forecastTransform = (forecast, forecastWritten, legacyUserSettings, print = false) => {
  const associated = forecast.associated;
  const writtenForecast = 'written' in forecastWritten.data && forecastWritten.data.written ?
    forecastWritten.data.written.map((written) => {
      const offset = associated.timezone.filter(zone =>
        zone.id === written.timezone.id)[0].offset;
      const title = format(getLocalDate(written.timestamp, offset), convertTokens('dddd M/D'));
      return {
        title,
        ...written,
      };
    }) : null;
  const units = {
    ...associated.units,
    global: associated.units.wave.height,
    wave: associated.units.wave.height,
    wind: associated.units.wind,
  };
  const forecastType = legacyUserSettings.type;
  const addTimeStep = (timeStep, localTimestamp) => ({
    ...timeStep,
    date: {
      local: format(localTimestamp, convertTokens('MMMM DD, YYYY HH:MM:SS')),
      timestamp: localTimestamp,
    },
    wave: (() => (
      timeStep.wave ? {
        ...timeStep.wave,
        height: {
          ...timeStep.wave.height,
          average: timeStep.wave.height.mean,
        },
        direction: {
          ...timeStep.wave.direction,
          iconPath: `${config.iconBaseUrl}${timeStep.wave.direction.cardinal}.gif`,
        },
      } : null
    ))(),
    wind: (() => (
      timeStep.wind ? {
        ...timeStep.wind,
        direction: {
          ...timeStep.wind.direction,
          iconPath: `${config.iconBaseUrl}${timeStep.wind.direction.cardinal}.gif`,
        },
      } : null
    ))(),
    weather: timeStep.weather,
  });
  let maxWave = 0;
  let maxWind = 0;
  const days = [];
  let timeSteps = [];

  const forecastData = forecast.data.forecasts;
  forecastData.forEach((timeStep, index) => {
    const windPeak = timeStep.wind ? timeStep.wind.speed.peak : 0;
    maxWind = windPeak > maxWind ? windPeak : maxWind;
    const wavePeak = timeStep.wave ? timeStep.wave.height.peak : 0;
    maxWave = wavePeak > maxWave ? wavePeak : maxWave;
    const offset = associated.timezone.filter(zone =>
      zone.id === timeStep.timezone.id)[0].offset;
    const currentStepTimestamp = getLocalDate(timeStep.timestamp, offset);
    const nextStepTimestamp = forecastData.length > index + 1 ? getLocalDate(
      forecastData[index + 1].timestamp,
      offset,
    ) : null;
    const date = format(currentStepTimestamp, convertTokens('dddd M/D'));
    const dateShort = format(currentStepTimestamp, convertTokens('ddd M/D'));

    const nextDate = nextStepTimestamp ? format(nextStepTimestamp, convertTokens('dddd M/D')) : null;
    if (date === nextDate) {
      timeSteps.push(addTimeStep(timeStep, currentStepTimestamp));
    } else {
      timeSteps.push(addTimeStep(timeStep, currentStepTimestamp));
      days.push({
        date,
        dateShort,
        timestamp: currentStepTimestamp,
        monthDay: format(currentStepTimestamp, convertTokens('M/D')),
        timeSteps,
      });
      timeSteps = [];
    }
  });
  const urls = {};
  forOwn(associated.urls, (value, key) => {
    urls[`${key}Url`] = value;
  });
  // API currently returns last day with one timestep. We only want full days.
  if (days[days.length - 1].timeSteps.length === 1) days.splice(-1, 1);
  const nearbyRegion = associated.region;
  const dateTime = new Date().getTime();
  const timestamp = Math.floor(dateTime / 1000);
  const currentLocalTime = getLocalDate(timestamp, associated.timezone[0].offset);

  const waveModelTimings = convertModelTimingsToForecastTimes(associated.waveModel, associated.timezone[0]);
  const weatherModelTimings = convertModelTimingsToForecastTimes(associated.weatherModel, associated.timezone[0]);

  const response = {
    waveModelTimings,
    weatherModelTimings,
    writtenForecast: print && writtenForecast.length > 2 ?
      writtenForecast.slice(0, 6) : writtenForecast,
    charts: {
      days,
      units,
      maxWave,
      maxWind,
      location: associated.location,
    },
    currentLocalTime,
    forecastValidTime: `${format(currentLocalTime, convertTokens('dddd, MMMM D'))} at ${format(currentLocalTime, convertTokens('h a'))}`,
    ...urls,
    nearbyRegion,
    forecastType,
    badData: maxWave === 0 && maxWind === 0,
  };
  return response;
};

export default forecastTransform;



// WEBPACK FOOTER //
// ./src/utils/forecasts/forecastTransform.js